import { useState } from 'react';
import { api, ApiError } from '../lib/api';
import { USD, int, magnitude, shortDate } from '../lib/format';
import { C, ink } from '../lib/theme';
import type { AuditExportResult } from '../lib/types';
import { ScaleModal, type Origin } from './ScaleModal';
import { IconAlert, IconCheck, IconUpload } from './icons';

/**
 * Confirmação + resultado do envio pro pipeline da Nekt (`webhook-6Pgb` →
 * `nekt_service.kory_revops_auditados_canal_direto[_resumo]`). Sempre manda
 * o universo completo do período (perda confirmada + anulado + VDI) — a
 * Nekt deduplica por `event_id` do lado dela (ver SKILL.md), então reexportar
 * um período sobreposto a um export anterior nunca duplica, só atualiza pra
 * a versão mais recente de cada evento.
 */
export function AuditExportModal({ open, origin, onClose, period }: {
  open: boolean;
  origin: Origin | null;
  onClose: () => void;
  period: { from?: string; to?: string };
}) {
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<AuditExportResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const canSend = !!period.from && !!period.to;

  async function handleClose() {
    setResult(null);
    setError(null);
    onClose();
  }

  async function handleExport() {
    if (!period.from || !period.to) return;
    setSending(true);
    setError(null);
    try {
      const res = await api.exportAudit({ from: period.from, to: period.to });
      setResult(res);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Falha ao exportar — tente de novo.');
    } finally {
      setSending(false);
    }
  }

  return (
    <ScaleModal open={open} origin={origin} onClose={handleClose} title="Exportar auditoria" width={460}>
      {!canSend && (
        <div style={{ fontSize: 13, color: ink(0.6) }}>Selecione um período (De/Até) no filtro antes de exportar.</div>
      )}

      {canSend && !result && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ fontSize: 13, color: ink(0.7), lineHeight: 1.5 }}>
            Envia todo o auditado (Perda Confirmada + Anulado + VDI) de{' '}
            <strong style={{ color: C.fg }}>{shortDate(period.from)}</strong> a{' '}
            <strong style={{ color: C.fg }}>{shortDate(period.to)}</strong> pro pipeline da Nekt (canal direto).
            Pode reexportar o mesmo período quantas vezes precisar — a versão mais recente de cada evento sempre substitui a anterior, sem duplicar.
          </div>

          {error && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', borderRadius: 11,
              background: 'rgba(242,85,90,0.1)', border: '1px solid rgba(242,85,90,0.3)', color: C.churn, fontSize: 12.5,
            }}><IconAlert size={14} />{error}</div>
          )}

          <button
            type="button" onClick={handleExport} disabled={sending}
            className="ko-primary"
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 9,
              border: 'none', background: `linear-gradient(150deg, ${C.green}, #1e9e57)`, color: '#08210F',
              boxShadow: '0 8px 22px -4px rgba(46,204,113,0.5)', cursor: sending ? 'wait' : 'pointer',
              font: 'inherit', fontSize: 13.5, fontWeight: 800, padding: '12px 14px', borderRadius: 12,
              opacity: sending ? 0.7 : 1,
            }}
          >
            <IconUpload size={15} />{sending ? 'Enviando…' : 'Exportar pro Nekt'}
          </button>
        </div>
      )}

      {result && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', borderRadius: 13,
            background: 'rgba(46,204,113,0.1)', border: '1px solid rgba(46,204,113,0.3)',
          }}>
            <span style={{
              width: 30, height: 30, borderRadius: '50%', flex: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'rgba(46,204,113,0.22)', color: C.green,
            }}><IconCheck size={15} /></span>
            <div>
              <div style={{ fontSize: 13.5, fontWeight: 750, color: C.fg }}>{int(result.events_count)} eventos diretos enviados</div>
              <div style={{ fontSize: 12, color: ink(0.5), marginTop: 1 }}>{USD(magnitude(result.total_valor))} no total</div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ flex: 1, padding: '9px 10px', borderRadius: 10, background: ink(0.04), textAlign: 'center' }}>
              <div style={{ fontSize: 15, fontWeight: 800, color: C.accent }}>{int(result.perda_confirmada_count)}</div>
              <div style={{ fontSize: 10, color: ink(0.45), marginTop: 2 }}>Perda Confirmada</div>
            </div>
            <div style={{ flex: 1, padding: '9px 10px', borderRadius: 10, background: ink(0.04), textAlign: 'center' }}>
              <div style={{ fontSize: 15, fontWeight: 800, color: C.green }}>{int(result.anulado_count)}</div>
              <div style={{ fontSize: 10, color: ink(0.45), marginTop: 2 }}>Anulado</div>
            </div>
            <div style={{ flex: 1, padding: '9px 10px', borderRadius: 10, background: ink(0.04), textAlign: 'center' }}>
              <div style={{ fontSize: 15, fontWeight: 800, color: C.blueSoft }}>{int(result.vdi_count)}</div>
              <div style={{ fontSize: 10, color: ink(0.45), marginTop: 2 }}>VDI</div>
            </div>
          </div>

          {Number(result.parceiro_count) > 0 && (
            <div style={{ fontSize: 12, color: ink(0.6), padding: '9px 12px', borderRadius: 10, background: ink(0.04) }}>
              + {int(result.parceiro_count)} eventos de parceiro enviados como estão
              {Number(result.parceiro_pendente_count) > 0 && ` (${int(result.parceiro_pendente_count)} ainda sem auditoria)`}
            </div>
          )}

          {!result.triggered && (
            <div style={{ fontSize: 12, color: ink(0.5) }}>
              Enviado, mas o processamento imediato falhou — a Nekt processa no próximo run automático.
            </div>
          )}
        </div>
      )}
    </ScaleModal>
  );
}
